/**
 * Box 2 (spec §3.4): one row per ad set, in the view's order, then "รวม" over every row (also while 15 are shown),
 * through D1's OverviewBox (loading, failed with "ลองอีกครั้ง", the data) and OverviewTable (numbers right-aligned, an
 * n<500 line faded, a wide table scrolling inside its box). Columns: Ad set · Channel · Segment · Spend · Share · Imp ·
 * CTR · CPC · CPM. Over SHOW_FIRST rows, "แสดง N จาก M ad set" and the toggle; the footnotes close the box, also under
 * an empty table (R9). NULL prints "—", never 0.
 *
 * @module components/audience/AudienceTable
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { OverviewBox } from '@/components/overview/OverviewBox';
import { LowSample, OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import { hasStateChip } from '@/services/audience/audienceRules';
import { emptyTextOf, type AppliedFilters } from '@/services/audience/audienceText';
import { SHOW_FIRST, type AudienceRow, type AudienceTableData, type AudienceTotal } from '@/services/audience/audienceTypes';
import { CHANNEL_LABEL, campaignStateText, familyLabel, type TFn } from '@/services/channels/channelFormat';
import type { TargetingFamily } from '@/services/channels/channelTypes';
import { fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import type { BoxState } from '@/services/overview/overviewTypes';
import { AudienceNotes } from './AudienceNotes';

const TOGGLE_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 print:hidden';
/** D3's chip: a word in a bordered pill, upright in a faded (italic) row, so none relies on colour alone. */
const CHIP = 'inline-block whitespace-nowrap rounded-full border px-1.5 text-[11px] not-italic print:whitespace-normal';
const PLAIN_CLASS = `${CHIP} border-neutral-400 text-neutral-800`;
const WARNING_CLASS = `${CHIP} border-warning-700 text-warning-700`;

/** A table line: one ad set, or "รวม" (OverviewTable has no footer, so the total is the last line, as on D2). */
type Line = { kind: 'row'; row: AudienceRow } | { kind: 'total'; total: AudienceTotal };

const metricsOf = (l: Line): AudienceTotal => (l.kind === 'row' ? l.row : l.total);

const bold = (on: boolean, node: React.ReactNode): React.ReactNode => (on ? <span className="font-semibold">{node}</span> : node);

/** The name on two lines at most (the whole name on hover, all of it on paper), then the status chip (R4). */
const AdSetCell: React.FC<{ row: AudienceRow }> = ({ row }) => {
  const { t } = useTranslation();
  return (
    <div className="w-64 whitespace-normal print:w-48">
      <span data-ad-set-name title={row.name} className="line-clamp-2 break-words print:line-clamp-none">{row.name}</span>
      {hasStateChip(row.state) && <span data-chip="state" className={`mt-1 ${PLAIN_CLASS}`}>{campaignStateText(row.state, t)}</span>}
    </div>
  );
};

/** The channel's dot and short name, as on D3. */
const ChannelCell: React.FC<{ row: AudienceRow }> = ({ row }) => (
  <span className="inline-flex items-center gap-1.5">
    <PlatformDot platform={row.platform} />
    {CHANNEL_LABEL[row.platform]}
  </span>
);

/** The family as a chip; Unclassified wears D2's warning colour. */
const SegmentChip: React.FC<{ family: TargetingFamily }> = ({ family }) => {
  const { t } = useTranslation();
  return <span data-segment={family} className={family === 'UNCLASSIFIED' ? WARNING_CLASS : PLAIN_CLASS}>{familyLabel(family, t)}</span>;
};

const columnsOf = (t: TFn, currency: string | null): Array<OverviewColumn<Line>> => {
  const num = (id: string, header: string, text: (m: AudienceTotal) => React.ReactNode): OverviewColumn<Line> => ({
    id, header, numeric: true, cell: (l) => bold(l.kind === 'total', text(metricsOf(l))),
  });
  const rowOnly = (id: string, header: string, cell: (r: AudienceRow) => React.ReactNode): OverviewColumn<Line> => ({
    id, header, cell: (l) => (l.kind === 'row' ? cell(l.row) : null),
  });
  return [
    {
      id: 'adSet', header: t('audience.col.adSet'),
      cell: (l) => (l.kind === 'row' ? <AdSetCell row={l.row} /> : <span className="font-semibold">{t('channels.breakdown.total')}</span>),
    },
    rowOnly('channel', t('audience.col.channel'), (r) => <ChannelCell row={r} />),
    rowOnly('segment', t('audience.col.segment'), (r) => <SegmentChip family={r.family} />),
    num('spend', t('audience.col.spend'), (m) => fmtMoney(m.spend, currency)),
    num('share', t('audience.col.share'), (m) => fmtPct(m.share, 1)),
    num('impressions', t('audience.col.impressions'), (m) => <>{fmtCount(m.impressions)}<LowSample on={m.lowSample} /></>),
    num('ctr', t('audience.col.ctr'), (m) => fmtPct(m.ctr)),
    num('cpc', t('audience.col.cpc'), (m) => fmtMoney(m.cpc, currency)),
    num('cpm', t('audience.col.cpm'), (m) => fmtMoney(m.cpm, currency)),
  ];
};

interface ShowingProps {
  shown: number;
  total: number;
  showAll: boolean;
  onShowAll: (on: boolean) => void;
}

/** "แสดง 15 จาก 40 ad set" (printed) and the toggle (not printed). */
const Showing: React.FC<ShowingProps> = ({ shown, total, showAll, onShowAll }) => {
  const { t } = useTranslation();
  return (
    <div data-audience-showing className="mt-3 flex flex-wrap items-center gap-3 text-sm text-neutral-700">
      <span>{t('audience.showing', { shown, total })}</span>
      <button type="button" className={TOGGLE_CLASS} onClick={() => onShowAll(!showAll)}>
        {showAll ? t('audience.showTop', { n: SHOW_FIRST }) : t('audience.showAll')}
      </button>
    </div>
  );
};

const linesOf = (data: AudienceTableData): Line[] => [
  ...data.shown.map((row): Line => ({ kind: 'row', row })),
  { kind: 'total', total: data.total },
];

interface Props {
  state: BoxState<AudienceTableData>;
  /** The page currency (D1's choice); null until the facts are read. */
  currency: string | null;
  /** The workspace's name, or "ทุก workspace": the empty line names it (R9). */
  scope: string;
  filters: AppliedFilters;
  /** footnotesOf(…), in order. */
  notes: readonly string[];
  showAll: boolean;
  onShowAll: (on: boolean) => void;
  onRetry: () => void;
}

export const AudienceTable: React.FC<Props> = ({ state, currency, scope, filters, notes, showAll, onShowAll, onRetry }) => {
  const { t } = useTranslation();
  return (
    <OverviewBox id="2" title={t('audience.box.table')} state={state} onRetry={onRetry} className="print:break-inside-auto">
      {(data) => (
        <>
          {data.rows.length === 0
            ? <BoxMessage text={emptyTextOf(data, scope, filters, t)} />
            : (
              <OverviewTable
                columns={columnsOf(t, currency)} rows={linesOf(data)}
                rowKey={(l) => (l.kind === 'row' ? `row:${l.row.key}` : 'total')} faded={(l) => metricsOf(l).lowSample}
              />
            )}
          {data.rows.length > SHOW_FIRST && <Showing shown={data.shown.length} total={data.rows.length} showAll={showAll} onShowAll={onShowAll} />}
          <AudienceNotes notes={notes} />
        </>
      )}
    </OverviewBox>
  );
};
